import { useEffect, useState } from 'react';
import { Card, Checkbox, Col, Form, Radio, Row, Typography } from 'antd';
import { VEHICLE_FIELDS } from '@features/vehicles/constants/vehicleFields';
import { useGetVehicleByBrandModelLine, useGetVehicleByFasecolda } from '@features/vehicles/hooks/useVehicles';
import { SelectPlus } from 'Components/SelectPlus';
import { SelectMunicipality } from '@features/locations/components/SelectMunicipality';
import { InputCurrency } from '@shared/components/inputs/InputCurrency';
import { getErrorMessage } from '@shared/utils/errors/getErrorMessage';
import { SelectServiceType } from '../../SelectServiceType';
import { InputPlate } from '../InputPlate';
import { SelectBrand } from './SelectBrand';
import { SelectVehicleClass } from '../../SelectVehicleClass';
import { SelectLine } from './SelectLine';
import { InputFasecoldaCode } from './InputFasecoldaCode';

const { Text } = Typography;

const UNKNOWN_FASECOLDA_CODE = 'unknownFasecoldaCode';

const CLASS_CATEGORY = {
  AUTOMOVIL: 'Automóvil',
  'BUS / BUSETA / MICROBUS': 'Pesados',
  CAMION: 'Pesados',
  'CAMIONETA PASAJ.': 'Automóvil',
  'CAMIONETA REPAR': 'Pickup',
  CAMPERO: 'SUV',
  CARROTANQUE: 'Pesados',
  CHASIS: 'Pesados',
  CUATRIMOTO: 'Motos',
  FURGON: 'Pesados',
  FURGONETA: 'Pesados',
  ISOCARRO: 'Motos',
  MOTOCARRO: 'Motos',
  MOTOCICLETA: 'Motos',
  'PICKUP DOBLE CAB': 'Pickup',
  'PICKUP SENCILLA': 'Pickup',
  REMOLCADOR: 'Pesados',
  REMOLQUE: 'Pesados',
  VOLQUETA: 'Pesados',
};

// La categoría no se persiste: se calcula a partir de la clase. En el fallback el campo se muestra deshabilitado, solo para reflejar lo que se dedujo del resto de la información
const CATEGORY_OPTIONS = [...new Set(Object.values(CLASS_CATEGORY))].map((name) => ({
  label: name,
  value: name,
}));

const normalize = (value) => value?.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '') ?? '';

const inferCategory = (vehicleClass) => {
  const normalizedClass = normalize(vehicleClass);
  return Object.entries(CLASS_CATEGORY)
    .sort((a, b) => b[0].length - a[0].length)
    .find(([key]) => normalizedClass.includes(normalize(key)))?.[1];
};

const validatePlate = (value) => {
  if (!value) return Promise.resolve();
  const formats = [
    /^[A-Z]{3}[0-9]{3}$/i,
    /^(CD|CC|AT|OI)[0-9]{4}$/i,
    /^[A-Z]{3}[0-9]{2}[A-Z]?$/i,
    /^[RS][0-9]{5}$/i,
  ];
  if (formats.some((format) => format.test(value))) return Promise.resolve();
  return Promise.reject(new Error('El formato de la placa no es válido'));
};

const isVehicleYear = (value) => /^(19|20)\d{2}$/.test(String(value ?? '').trim());

const findModelValue = (values, year) => values.find((entry) => String(entry.model) === String(year));

export const VehicleFormFallback = ({
  form,
  initialValues,
  cardProps = {},
  onCirculationCityChange,
  ...formProps
}) => {
  const [plateReady, setPlateReady] = useState(!!initialValues);
  const [formUnlocked, setFormUnlocked] = useState(!!initialValues);
  const [manualCascade, setManualCascade] = useState(false);
  const [lockedByFasecolda, setLockedByFasecolda] = useState(false);
  const [brandModels, setBrandModels] = useState(
    initialValues?.model ? [initialValues.model] : [],
  );
  const [fasecoldaModelValues, setFasecoldaModelValues] = useState([]);
  const [customModel, setCustomModel] = useState(null); // Modelo ingresado manualmente que aún no está en el catálogo, para mostrarlo como opción
  const [shouldResolveLine, setShouldResolveLine] = useState(false); // Solo resuelve la referencia al seleccionarla manualmente, para evitar consultas al cargar o buscar por placa

  const hasPlate = Form.useWatch(VEHICLE_FIELDS.HAS_PLATE, form);
  const plate = Form.useWatch(VEHICLE_FIELDS.PLATE, form);
  const unknownCode = Form.useWatch(UNKNOWN_FASECOLDA_CODE, form);
  const fasecoldaCode = Form.useWatch(VEHICLE_FIELDS.FASECOLDA_CODE, form);
  const brand = Form.useWatch(VEHICLE_FIELDS.BRAND, form);
  const model = Form.useWatch(VEHICLE_FIELDS.MODEL, form);
  const line = Form.useWatch(VEHICLE_FIELDS.LINE, form);

  const manualMode = !hasPlate && unknownCode;
  const fieldReady = (dependency) => {
    if (manualCascade) return dependency;
    return formUnlocked || (manualMode && dependency);
  };

  const knownModelEntry = findModelValue(fasecoldaModelValues, model);
  const needsModelLookup = lockedByFasecolda
    && !!fasecoldaCode
    && isVehicleYear(model)
    && !knownModelEntry;

  const modelQuery = useGetVehicleByFasecolda({ fasecoldaCode, model }, { enabled: needsModelLookup });

  const lineQuery = useGetVehicleByBrandModelLine({ brand, model, line }, { enabled: shouldResolveLine && !lockedByFasecolda && !!brand && !!model && !!line });

  const modelValues = lockedByFasecolda && fasecoldaModelValues.length
    ? fasecoldaModelValues.map(({ model: year }) => year)
    : brandModels;
  const modelOptions = [
    ...modelValues.map((year) => ({
      label: String(year),
      value: String(year),
    })),
    ...(customModel && !modelValues.some((year) => String(year) === String(customModel))
      ? [{ label: String(customModel), value: String(customModel) }]
      : []),
  ];

  const modelLoading = needsModelLookup && modelQuery.isFetching;
  const modelError = lockedByFasecolda && !!model && !knownModelEntry && !modelLoading && (!isVehicleYear(model) || (needsModelLookup && modelQuery.isError));

  const lineLoading = shouldResolveLine && lineQuery.isFetching;
  const lineError = shouldResolveLine && !lineQuery.isFetching && lineQuery.isError;

  const clearVehicleData = (preserveCode = false) => {
    form.setFieldsValue({
      [VEHICLE_FIELDS.FASECOLDA_CODE]: preserveCode
        ? form.getFieldValue(VEHICLE_FIELDS.FASECOLDA_CODE)
        : undefined,
      [VEHICLE_FIELDS.BRAND]: undefined,
      [VEHICLE_FIELDS.MODEL]: undefined,
      [VEHICLE_FIELDS.LINE]: undefined,
      [VEHICLE_FIELDS.CLASS]: undefined,
      [VEHICLE_FIELDS.INSURED_VALUE]: undefined,
      [VEHICLE_FIELDS.CATEGORY]: undefined,
    });
    setBrandModels([]);
    setFasecoldaModelValues([]);
    setCustomModel(null);
    setShouldResolveLine(false);
  };

  const setVehicle = (vehicle = {}) => {
    const values = vehicle.modelValues ?? [];
    const firstModel = values[0];
    const selectedModel = firstModel?.model ?? vehicle.model;
    setFasecoldaModelValues(values);
    if (!values.length && selectedModel) setBrandModels([selectedModel]);
    setCustomModel(null);
    setShouldResolveLine(false);

    form.setFieldsValue({
      [VEHICLE_FIELDS.FASECOLDA_CODE]: vehicle.fasecoldaCode || undefined,
      [VEHICLE_FIELDS.BRAND]: vehicle.brand || undefined,
      [VEHICLE_FIELDS.MODEL]: selectedModel || undefined,
      [VEHICLE_FIELDS.LINE]: vehicle.line || undefined,
      [VEHICLE_FIELDS.CLASS]: vehicle.class || undefined,
      [VEHICLE_FIELDS.INSURED_VALUE]: firstModel?.insuredValue || vehicle.insuredValue || undefined,
      [VEHICLE_FIELDS.CATEGORY]:
        inferCategory(vehicle.class) || vehicle.category || undefined,
    });
  };

  const handlePlateQuery = ({ data, isFetching, isError }) => {
    if (isFetching) {
      setPlateReady(false);
      return;
    }

    if (isError) {
      clearVehicleData();
      form.setFieldsValue({ [UNKNOWN_FASECOLDA_CODE]: false });
    } else {
      setVehicle(data);
      form.setFieldsValue({ [UNKNOWN_FASECOLDA_CODE]: true });
      setFormUnlocked(true);
      setManualCascade(false);
      setLockedByFasecolda(false);
    }
    setPlateReady(true);
  };

  const handleFasecoldaQuery = ({ data, isFetching, isError }) => {
    if (isFetching) return;
    if (isError || !data) {
      clearVehicleData(true);
      setFormUnlocked(false);
      setLockedByFasecolda(false);
      setManualCascade(false);
      return;
    }
    setVehicle(data);
    setFormUnlocked(true);
    setManualCascade(false);
    setLockedByFasecolda(true);
  };

  const handleBrandSelect = (_value, option) => {
    setBrandModels(option?.record?.models ?? []);
  };

  const handleModelSearch = (text) => {
    if (!lockedByFasecolda) return;
    const year = text?.trim();
    if (isVehicleYear(year) && !modelValues.some((listedYear) => String(listedYear) === year)) {
      setCustomModel(year);
    }
  };

  useEffect(() => { // Modelo faltante en catálogo Fasecolda: al resolver, se agrega a la lista y se setea el valor asegurado
    if (!needsModelLookup || modelQuery.isFetching || modelQuery.isError) return;

    const fetched = modelQuery.data?.modelValues?.[0];
    if (!fetched || String(fetched.model) !== String(model)) return;

    setFasecoldaModelValues((prev) => {
      if (findModelValue(prev, fetched.model)) return prev;
      return [...prev, fetched].sort((a, b) => Number(b.model) - Number(a.model));
    });
    setCustomModel(null);
    form.setFieldsValue({ [VEHICLE_FIELDS.INSURED_VALUE]: fetched.insuredValue });
  }, [needsModelLookup, model, modelQuery.isFetching, modelQuery.isError, modelQuery.data]);

  useEffect(() => { // Referencia elegida a mano: resuelve código Fasecolda, clase y valor asegurado
    if (!shouldResolveLine || lineQuery.isFetching || lineQuery.isError || !lineQuery.data) return;

    form.setFieldsValue({
      [VEHICLE_FIELDS.FASECOLDA_CODE]: lineQuery.data.fasecoldaCode,
      [VEHICLE_FIELDS.CLASS]: lineQuery.data.class,
      [VEHICLE_FIELDS.CATEGORY]: inferCategory(lineQuery.data.class),
      [VEHICLE_FIELDS.INSURED_VALUE]: lineQuery.data.insuredValue,
    });
  }, [shouldResolveLine, lineQuery.isFetching, lineQuery.isError, lineQuery.data]);

  const handleValuesChange = (changedValues, allValues) => {
    if (VEHICLE_FIELDS.HAS_PLATE in changedValues) {
      form.setFieldsValue({
        [VEHICLE_FIELDS.PLATE]: allValues[VEHICLE_FIELDS.HAS_PLATE] ? '' : 'XXX000',
      });
    }

    if (UNKNOWN_FASECOLDA_CODE in changedValues) {
      clearVehicleData();
      setLockedByFasecolda(false);
      setManualCascade(changedValues[UNKNOWN_FASECOLDA_CODE]);
      if (!changedValues[UNKNOWN_FASECOLDA_CODE]) setFormUnlocked(false);
    }

    if (VEHICLE_FIELDS.BRAND in changedValues) {
      setShouldResolveLine(false);
      form.setFieldsValue({
        [VEHICLE_FIELDS.MODEL]: undefined,
        [VEHICLE_FIELDS.LINE]: undefined,
        [VEHICLE_FIELDS.CLASS]: undefined,
        [VEHICLE_FIELDS.FASECOLDA_CODE]: undefined,
        [VEHICLE_FIELDS.INSURED_VALUE]: undefined,
        [VEHICLE_FIELDS.CATEGORY]: undefined,
      });
    }

    if (VEHICLE_FIELDS.MODEL in changedValues) {
      if (!lockedByFasecolda) {
        setShouldResolveLine(false);
        form.setFieldsValue({
          [VEHICLE_FIELDS.LINE]: undefined,
          [VEHICLE_FIELDS.CLASS]: undefined,
          [VEHICLE_FIELDS.FASECOLDA_CODE]: undefined,
          [VEHICLE_FIELDS.INSURED_VALUE]: undefined,
          [VEHICLE_FIELDS.CATEGORY]: undefined,
        });
      } else {
        const selectedModel = changedValues[VEHICLE_FIELDS.MODEL];
        const existing = findModelValue(fasecoldaModelValues, selectedModel);
        form.setFieldsValue({
          [VEHICLE_FIELDS.INSURED_VALUE]: existing?.insuredValue,
        });
      }
    }

    if (VEHICLE_FIELDS.LINE in changedValues) {
      setShouldResolveLine(!!changedValues[VEHICLE_FIELDS.LINE]);
    }
  };

  const modelExtra = modelLoading ? (
    <Text type='secondary' style={{ fontSize: '0.9em' }}>Consultando el modelo...</Text>
  ) : modelError ? (
    <Text type='danger' style={{ fontSize: '0.9em' }}>No se encontró información del modelo</Text>
  ) : undefined;

  const referenceExtra = lineLoading ? (
    <Text type='secondary' style={{ fontSize: '0.9em' }}>Resolviendo código y clase...</Text>
  ) : lineError ? (
    <Text type='danger' style={{ fontSize: '0.9em' }}>{getErrorMessage(lineQuery.error)}</Text>
  ) : undefined;

  return (
    <Card
      {...cardProps}
      styles={{
        ...cardProps.styles,
        header: { textAlign: 'center', ...cardProps.styles?.header },
      }}
    >
      <Form
        layout='vertical'
        form={form}
        onValuesChange={handleValuesChange}
        initialValues={{
          [VEHICLE_FIELDS.HAS_PLATE]: true,
          [UNKNOWN_FASECOLDA_CODE]: !!initialValues,
          [VEHICLE_FIELDS.IS_NEW]: false,
          [VEHICLE_FIELDS.IS_UTILITARIAN]: false,
          ...initialValues,
          [VEHICLE_FIELDS.CATEGORY]: inferCategory(initialValues?.class), // Se infiere la categoría a partir de la clase
        }}
        {...formProps}
      >
        <Row gutter={16} align='top'>
          <Col xs={24} md={12}>
            <Form.Item label='Placa' required>
              <Form.Item
                name={VEHICLE_FIELDS.PLATE}
                rules={[
                  { required: hasPlate },
                  { validator: (_, value) => validatePlate(value) },
                  {
                    validator: () => {
                      if (hasPlate && plate && !plateReady) {
                        return Promise.reject(new Error('No se ha consultado la placa aún'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                validateTrigger={[]}
                noStyle
              >
                <InputPlate
                  onVehicleByPlateQuery={handlePlateQuery}
                  validator={validatePlate}
                  disabled={!hasPlate || !!initialValues}
                />
              </Form.Item>
              <Form.Item
                name={VEHICLE_FIELDS.HAS_PLATE}
                valuePropName='checked'
                getValueProps={(value) => ({ checked: !value })}
                normalize={(checked) => !checked}
                noStyle
                hidden={!!initialValues}
              >
                <Checkbox style={{ marginTop: 4 }}>No conozco la placa</Checkbox>
              </Form.Item>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item label='Código Fasecolda' required>
              <Form.Item
                name={VEHICLE_FIELDS.FASECOLDA_CODE}
                rules={[{ required: true }]}
                validateTrigger={[]}
                noStyle
              >
                <InputFasecoldaCode
                  onVehicleByFasecoldaCodeQuery={handleFasecoldaQuery}
                  disabled={unknownCode || (!plateReady && hasPlate)}
                />
              </Form.Item>
              <Form.Item name={UNKNOWN_FASECOLDA_CODE} valuePropName='checked' noStyle>
                <Checkbox style={{ marginTop: 4 }} disabled={!plateReady && hasPlate}>
                  No conozco el código Fasecolda
                </Checkbox>
              </Form.Item>
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label='Categoría'
              name={VEHICLE_FIELDS.CATEGORY}
              rules={[{ required: true }]}
            >
              <SelectPlus options={CATEGORY_OPTIONS} placeholder='Categoría' disabled />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label='Estado del vehículo'
              name={VEHICLE_FIELDS.IS_NEW}
              rules={[{ required: true }]}
            >
              <Radio.Group
                options={[
                  { label: 'Usado', value: false },
                  { label: 'Nuevo', value: true },
                ]}
                optionType='button'
                buttonStyle='solid'
                block
                disabled={!fieldReady(true)}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item label='Marca' name={VEHICLE_FIELDS.BRAND} rules={[{ required: true }]}>
              <SelectBrand
                disabled={lockedByFasecolda || !fieldReady(true)}
                onSelect={handleBrandSelect}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label='Modelo'
              name={VEHICLE_FIELDS.MODEL}
              rules={[{ required: true }]}
              tooltip={lockedByFasecolda
                ? 'Si el año no aparece, ingréselo manualmente para realizar la consulta.'
                : undefined}
              extra={modelExtra}
            >
              <SelectPlus
                options={modelOptions}
                placeholder={lockedByFasecolda ? 'Seleccione o ingrese el modelo' : 'Modelo'}
                filterSort={(a, b) => Number(b.label) - Number(a.label)}
                onSearch={handleModelSearch}
                loading={modelLoading}
                disabled={lockedByFasecolda
                  ? !formUnlocked // Solo tras búsqueda Fasecolda exitosa
                  : !fieldReady(!!brand) || !modelOptions.length}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label='Referencia'
              name={VEHICLE_FIELDS.LINE}
              rules={[{ required: true }]}
              extra={referenceExtra}
            >
              <SelectLine
                brand={brand}
                model={model}
                disabled={lockedByFasecolda || !fieldReady(!!brand && !!model)}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item label='Clase' name={VEHICLE_FIELDS.CLASS} rules={[{ required: true }]}>
              <SelectVehicleClass disabled />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label='Ciudad de circulación'
              name={VEHICLE_FIELDS.CIRCULATION_CITY_ID}
              rules={[{ required: true }]}
            >
              <SelectMunicipality
                placeholder='Ciudad de circulación'
                onChange={onCirculationCityChange}
                disabled={!line}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label='Tipo de servicio'
              name={VEHICLE_FIELDS.SERVICE}
              rules={[{ required: true }]}
            >
              <SelectServiceType disabled={!line} />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label='Valor asegurado'
              name={VEHICLE_FIELDS.INSURED_VALUE}
              rules={[{ required: true }]}
            >
              <InputCurrency
                placeholder='Ingrese valor asegurado'
                numberType='positive'
                disabled={!line}
              />
            </Form.Item>
          </Col>

          <Col xs={24} md={12}>
            <Form.Item
              label='Valor de accesorios'
              name={VEHICLE_FIELDS.ACCESSORIES_VALUE}
              rules={[{ required: true }]}
            >
              <InputCurrency
                placeholder='Ingresar valor de accesorios'
                numberType='positive'
                disabled={!line}
              />
            </Form.Item>
          </Col>

          <Col xs={24}>
            <Form.Item
              label='¿Es utilitario?'
              name={VEHICLE_FIELDS.IS_UTILITARIAN}
              rules={[{ required: true }]}
            >
              <Radio.Group
                options={[
                  { label: 'Sí', value: true },
                  { label: 'No', value: false },
                ]}
                optionType='button'
                buttonStyle='solid'
                disabled={!line}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Card>
  );
};
